/**
 * The shapes the prima nota screen reads, and the guards that keep a reshaped payload off the
 * render path.
 *
 * The same reason the other finance views validate: this workspace mounts no error boundary, so a
 * single unguarded property access on a malformed response replaces the whole Finance route with
 * the router's default error screen instead of the module's own message.
 */

import { isNullableString, isRecord } from '../finance-view-guards';

export type Gap = {
  code: string;
  subject: string | null;
  detail: string;
  blocking: boolean;
};

/**
 * The two formats every account's month arrives in (decision 71): the CSV feeds the matching and
 * the PDF confirms it. In this order on the screen, so the two slots of an account always read the
 * same way.
 */
export const STATEMENT_FORMATS = ['pdf', 'csv'] as const;
export type StatementFormat = (typeof STATEMENT_FORMATS)[number];

const isStatementFormat = (value: unknown): value is StatementFormat =>
  value === 'pdf' || value === 'csv';

/** What one sweep of the statement archive concluded about one format of one account. */
export type SweepObservation = {
  accountKey: string;
  /** The format looked for. Absent from a server that predates it, which looked for PDFs only. */
  format?: StatementFormat;
  outcome:
    | 'imported'
    | 'already-held'
    | 'already-supplied'
    | 'not-found'
    | 'ambiguous'
    | 'unreadable'
    | 'blocked-by-existing'
    | 'unreachable';
  sweptAt: string;
  fileNames: string[];
  unrecognisedNames: string[];
  detail: string;
};

/**
 * The archive's word on one account's statement. `lastSweep` null means nobody has looked yet,
 * which the screen must say as exactly that and never as "not found".
 */
export type StatementArchive = {
  dueFrom: string;
  lateness: 'not-yet-expected' | 'late';
  lastSweep: SweepObservation | null;
};

/** One format of an account's statement: whether it arrived, and what the archive said. */
export type StatementFormatState = {
  supplied: boolean;
  /** Null for an account mapped to no folder. */
  archive: StatementArchive | null;
};

export type StatementState = {
  accountKey: string;
  accountLabel: string;
  /** Whether a statement arrived in either format. */
  supplied: boolean;
  /** Each format on its own. Absent from a server that predates them, which knew one statement. */
  pdf?: StatementFormatState;
  csv?: StatementFormatState;
  /** The one statement's archive, from such a server. Null for an account mapped to no folder. */
  archive?: StatementArchive | null;
};

export type SweepResult = {
  period: string;
  archiveDeclared: boolean;
  accounts: SweepObservation[];
  readiness: Readiness;
};

export type PeriodSource = {
  id: string;
  kind: 'fiscal-workbook' | 'bank-statement';
  accountKey: string | null;
  filename: string;
  contentHash: string;
  byteSize: number;
  uploadedBy: string;
  uploadedAt: string;
  withdrawnAt: string | null;
  withdrawnReason: string | null;
  /** A statement's format, judged on its bytes. Null for the documents file; absent from older servers. */
  format?: StatementFormat | null;
};

export type Readiness = {
  period: string;
  profileRevision: number | null;
  gaps: Gap[];
  statements: StatementState[];
  sources: PeriodSource[];
  suppliedCount: number;
  withdrawnCount: number;
  canGenerate: boolean;
  /** Whether the documents file can be fetched rather than uploaded. Absent from older servers. */
  fiscalExportConfigured?: boolean;
};

export type Control = { control: string; state: string; detail?: string };
export type Anomaly = { documentReference: string; code: string; detail: string };
export type RejectedRow = { source: string; row: number; reason: string };

/**
 * A bank movement no document claimed, as the screen has to show it to be able to ask about it.
 *
 * `treatment` is null when nobody classified it, and null is what the screen acts on: an item with
 * a treatment is a decision, an item without one is a gap that prints as "da classificare" in the
 * register the accountant receives.
 */
export type OpenItem = {
  date: string | null;
  accountKey: string;
  type: string;
  amountMinor: number;
  text: string;
  treatment: string | null;
};

/** A movement one of the two formats of a statement carries and the other does not. */
export type UnpairedMovement = {
  file: string;
  date: string | null;
  amountMinor: number;
  text: string;
};

/**
 * How one account's PDF and CSV compared, and which of them the matching read. A warning, never a
 * refusal: what a disagreement costs is already counted where it lands.
 */
export type StatementFormatCheck = {
  accountKey: string;
  outcome: 'pdf-only' | 'csv-only' | 'agree' | 'disagree' | 'not-compared';
  pdfFiles: string[];
  csvFiles: string[];
  paired: number;
  compared: { from: string; to: string } | null;
  onlyInCsv: UnpairedMovement[];
  onlyInPdf: UnpairedMovement[];
  fromPdfOutsideCsv: number;
};

/** One declared cost the fiscal source carries no document for. Case F of the standard. */
export type DeclarationOnlyItem = {
  date: string;
  counterparty: string;
  ref: string;
  amount: string;
  treatment: string;
};

export type ProducedRegister = {
  period: string;
  filename: string;
  workbookBase64: string;
  readiness: Readiness;
  receipt: {
    rowCount: number;
    controls: Control[];
    anomalies: Anomaly[];
    rejectedRows: RejectedRow[];
    openItems: OpenItem[];
    /** Absent from a server that predates the two formats. */
    statementFormats?: StatementFormatCheck[];
  };
  vouchable: boolean;
  vouchFailures: string[];
};

function isGap(value: unknown): value is Gap {
  return (
    isRecord(value) &&
    typeof value.code === 'string' &&
    isNullableString(value.subject) &&
    typeof value.detail === 'string' &&
    typeof value.blocking === 'boolean'
  );
}

const SWEEP_OUTCOMES = new Set([
  'imported',
  'already-held',
  'already-supplied',
  'not-found',
  'ambiguous',
  'unreadable',
  'blocked-by-existing',
  'unreachable',
]);

const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

export function isSweepObservation(value: unknown): value is SweepObservation {
  return (
    isRecord(value) &&
    typeof value.accountKey === 'string' &&
    (value.format === undefined || isStatementFormat(value.format)) &&
    typeof value.outcome === 'string' &&
    SWEEP_OUTCOMES.has(value.outcome) &&
    typeof value.sweptAt === 'string' &&
    isStringList(value.fileNames) &&
    isStringList(value.unrecognisedNames) &&
    typeof value.detail === 'string'
  );
}

function isStatementArchive(value: unknown): value is StatementArchive {
  return (
    isRecord(value) &&
    typeof value.dueFrom === 'string' &&
    (value.lateness === 'not-yet-expected' || value.lateness === 'late') &&
    (value.lastSweep === null || isSweepObservation(value.lastSweep))
  );
}

function isStatementFormatState(value: unknown): value is StatementFormatState {
  return (
    isRecord(value) &&
    typeof value.supplied === 'boolean' &&
    (value.archive === null || isStatementArchive(value.archive))
  );
}

function isStatementState(value: unknown): value is StatementState {
  return (
    isRecord(value) &&
    typeof value.accountKey === 'string' &&
    typeof value.accountLabel === 'string' &&
    typeof value.supplied === 'boolean' &&
    (value.pdf === undefined || isStatementFormatState(value.pdf)) &&
    (value.csv === undefined || isStatementFormatState(value.csv)) &&
    (value.archive === undefined || value.archive === null || isStatementArchive(value.archive))
  );
}

export function isSweepResult(value: unknown): value is SweepResult {
  return (
    isRecord(value) &&
    typeof value.period === 'string' &&
    typeof value.archiveDeclared === 'boolean' &&
    Array.isArray(value.accounts) &&
    value.accounts.every(isSweepObservation) &&
    isReadiness(value.readiness)
  );
}

function isPeriodSource(value: unknown): value is PeriodSource {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    (value.kind === 'fiscal-workbook' || value.kind === 'bank-statement') &&
    isNullableString(value.accountKey) &&
    typeof value.filename === 'string' &&
    typeof value.byteSize === 'number' &&
    typeof value.uploadedAt === 'string' &&
    isNullableString(value.withdrawnAt) &&
    isNullableString(value.withdrawnReason) &&
    (value.format === undefined || value.format === null || isStatementFormat(value.format))
  );
}

export function isReadiness(value: unknown): value is Readiness {
  return (
    isRecord(value) &&
    typeof value.period === 'string' &&
    (value.profileRevision === null || typeof value.profileRevision === 'number') &&
    Array.isArray(value.gaps) &&
    value.gaps.every(isGap) &&
    Array.isArray(value.statements) &&
    value.statements.every(isStatementState) &&
    Array.isArray(value.sources) &&
    value.sources.every(isPeriodSource) &&
    typeof value.suppliedCount === 'number' &&
    typeof value.withdrawnCount === 'number' &&
    typeof value.canGenerate === 'boolean' &&
    (value.fiscalExportConfigured === undefined ||
      typeof value.fiscalExportConfigured === 'boolean')
  );
}

function isProducedReceipt(receipt: unknown): boolean {
  return (
    isRecord(receipt) &&
    typeof receipt.rowCount === 'number' &&
    Array.isArray(receipt.controls) &&
    Array.isArray(receipt.anomalies) &&
    Array.isArray(receipt.rejectedRows) &&
    Array.isArray(receipt.openItems) &&
    receipt.openItems.every(isOpenItem) &&
    (receipt.statementFormats === undefined ||
      (Array.isArray(receipt.statementFormats) &&
        receipt.statementFormats.every(isStatementFormatCheck)))
  );
}

export function isProducedRegister(value: unknown): value is ProducedRegister {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.period === 'string' &&
    typeof value.filename === 'string' &&
    typeof value.workbookBase64 === 'string' &&
    isReadiness(value.readiness) &&
    typeof value.vouchable === 'boolean' &&
    Array.isArray(value.vouchFailures) &&
    value.vouchFailures.every((failure) => typeof failure === 'string') &&
    isProducedReceipt(value.receipt)
  );
}

const STATEMENT_FORMAT_OUTCOMES = new Set([
  'pdf-only',
  'csv-only',
  'agree',
  'disagree',
  'not-compared',
]);

function isUnpairedMovement(value: unknown): value is UnpairedMovement {
  return (
    isRecord(value) &&
    typeof value.file === 'string' &&
    isNullableString(value.date) &&
    typeof value.amountMinor === 'number' &&
    typeof value.text === 'string'
  );
}

function isComparedRange(compared: unknown): boolean {
  return (
    compared === null ||
    (isRecord(compared) && typeof compared.from === 'string' && typeof compared.to === 'string')
  );
}

function isStatementFormatCheck(value: unknown): value is StatementFormatCheck {
  return (
    isRecord(value) &&
    typeof value.accountKey === 'string' &&
    typeof value.outcome === 'string' &&
    STATEMENT_FORMAT_OUTCOMES.has(value.outcome) &&
    isStringList(value.pdfFiles) &&
    isStringList(value.csvFiles) &&
    typeof value.paired === 'number' &&
    isComparedRange(value.compared) &&
    Array.isArray(value.onlyInCsv) &&
    value.onlyInCsv.every(isUnpairedMovement) &&
    Array.isArray(value.onlyInPdf) &&
    value.onlyInPdf.every(isUnpairedMovement) &&
    typeof value.fromPdfOutsideCsv === 'number'
  );
}

/**
 * Each format of an account's statement, or null from a server that predates the two formats —
 * which knew one statement per account, and which the screen then shows as it always did.
 */
export function formatStatesOf(
  statement: StatementState,
): Record<StatementFormat, StatementFormatState> | null {
  if (statement.pdf !== undefined && statement.csv !== undefined) {
    return { pdf: statement.pdf, csv: statement.csv };
  }
  return null;
}

function isOpenItem(value: unknown): value is OpenItem {
  return (
    isRecord(value) &&
    isNullableString(value.date) &&
    typeof value.accountKey === 'string' &&
    typeof value.type === 'string' &&
    typeof value.amountMinor === 'number' &&
    typeof value.text === 'string' &&
    isNullableString(value.treatment)
  );
}

/**
 * Turn one open item's own text into a pattern that matches it and nothing else.
 *
 * The server keys treatments by REGULAR EXPRESSION, matched against the movement's text, because a
 * bank line has no identifier to key on. That contract is right for the API and impossible for the
 * screen: nobody is going to hand an accountant a regex box, and an unescaped description
 * containing a `(` is not merely wrong, it throws where the server compiles it.
 *
 * So the screen classifies an ITEM and derives the pattern here — every metacharacter escaped,
 * anchored at both ends. The API keeps accepting broader patterns from callers who want them.
 *
 * ONE CONSEQUENCE, STATED RATHER THAN HIDDEN: two movements whose text is byte-identical share one
 * pattern and therefore one treatment. That is not a defect of this function but of what a bank
 * line carries — the semicolon dialect has neither a transaction id nor an account column — and it
 * is the same collision the supply step already warns about for duplicate uploads.
 */
export function literalPattern(text: string): string {
  return `^${text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`;
}

/**
 * The month before the one the operator is standing in.
 *
 * The default rather than the current month, because a register is produced once its period has
 * closed — offering the open month first would make the ordinary case a correction.
 */
export function previousPeriod(today: Date): string {
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-based, so this is already the previous month's index + 1
  const shifted = month === 0 ? { year: year - 1, month: 12 } : { year, month };
  return `${shifted.year}-${String(shifted.month).padStart(2, '0')}`;
}

/** A size a person can read, rather than a byte count. */
export function readableSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Turn base64 into the bytes a download needs.
 *
 * Written out rather than fetched as a data URL because the workbook arrives beside its receipt in
 * one response, and the alternative — a second request that derives the register again — could
 * hand over a file that does not match the findings shown beside it.
 */
export function base64ToBlob(base64: string, type: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type });
}
